import React from "react";
import { withStyles, withTheme } from "@material-ui/core/styles";
import { InlineTimePicker, MuiPickersUtilsProvider } from "material-ui-pickers";
import { compose } from "recompose";
import Time from "@material-ui/icons/AccessTime";
import Clear from "@material-ui/icons/CancelOutlined";
import InputAdornment from "@material-ui/core/InputAdornment";
import appConstants from "../../../../../appConstants";
import DateFnsUtils from "@date-io/date-fns";
import { isValid as isDateTimeValid, parse, format } from "date-fns";

const styles = (theme) => ({
  container: {
    backgroundColor: theme.ModalBGColor,
    minHeight: "24px",
    boxSizing: "border-box",
    marginTop: 6,
    display: "flex",
    flexDirection: "row",
    maxWidth: "220px",
    "&>div": {
      margin: "0px 5px",
    },
    '& $inputRoot input$inputStyle[type="text"]': {
      fontSize: theme.textFontSize,
      color: theme.TextColor,
      border: "none",
      height: 24,
      paddingBottom: 0,
      "&:focus": {
        border: "none",
        paddingBottom: 0,
      },
      "&::placeholder": {
        color: "currentColor",
      },
    },
    // '& span[class*="MuiPickersClockNumber-clockNumber"]':{
    //     color: theme.TextColor
    // },
    // '& p[class*="MuiFormHelperText-root"][class*="MuiFormHelperText-error"]':{
    //     backgroundColor: theme.ComponentHeader,
    //     marginTop:0,
    //     paddingTop:8,
    //     color: theme.ErrorMessageColor,
    //     fontSize:theme.textFontSize
    // },
    "& $inputRoot": {
      marginTop: 0,
    },
  },
  dateTimeContainer: {
    maxWidth: "175px",
    flexDirection: "column",
  },
  paperStyle: {
    backgroundColor: theme.ModalBGColor,
    color: theme.TextColor,
  },
  labelProps: {
    fontSize: theme.textFontSize,
    color: theme.iconColor,
    marginTop: -16,
  },
  labelShrink: {
    marginTop: -8,
  },
  inputStyle: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    padding: 0,
  },
  inputRoot: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    borderBottom: `1px solid ${theme.iconColor}`,
    maxHeight: 24,
    "&::before, &::after": {
      border: "none !important",
    },
    "&$inputFocus": {
      borderBottom: `1px solid ${theme.HeaderSelectedText}`,
    },
  },
  inputFocus: {},
  iconColor: {
    color: theme.iconColor,
  },
  inputAdornment: {
    cursor: "pointer",
    marginBottom: 0,
  },
  errorMessage: {
    display: "none",
  },
  errorMessageVisible: {
    display: "block",
    marginBottom: 0,
    color: theme.ErrorMessageColor,
    fontSize: theme.textFontSize,
  },
  editorContainer: {
    backgroundColor: theme.ComponentHeader,
    paddingTop: 2,
    border: `1px solid ${theme.iconColor}`,
    marginTop: -4,
    alignItems: "center",
    display: "flex",
  },
  roundBorderContainer: {
    position: 'relative',
    borderRadius: 4,
    backgroundColor: theme.ComponentHeader,
    border: `1px solid ${theme.iconColor}`,
    alignItems: "center",
    display: "flex",
    minHeight: 32,
  },
  closedDayInputStyle: {
    color: theme.SelectedStopBorderColor,
  },
  closedDayEditorContainer:{
    backgroundColor: theme.ComponentHeader,
    marginTop:-4
  },
  containerLabelClass: {
    padding: "0 2",
    position: "absolute",
    backgroundColor: theme.ModalBGColor,
    fontSize: 11,
    color: theme.iconColor,
    top: '-9px',
    margin: '0 8px',
  },
});

class TimeRange extends React.Component {
  constructor(props) {
    super(props);
    this.timeConfig = appConstants.DATETYPES.TIME;
    this.minTimeErrorElement = null;
    this.endTimeInputRef = React.createRef();
    this.state = {
      startTime: null,
      endTime: null,
    };
  }

  componentDidMount() {
    this.updateTime();
  }

  updateTime = () => {
    const [startTime, endTime] =
      this.props.value && this.props.value.split("-").length === 2
        ? this.props.value
            .split("-")
            .map((time) =>
              parse(time.trim(), this.timeConfig.format, new Date())
            )
        : [null, null];
    this.setState({
      startTime,
      endTime,
    });
    this.updateParentValue(this.props.value);
  };

  startDateChanged = (startTime) => {
    this.setState(
      {
        startTime,
      },
      this.validateTimeWindow
    );
  };

  endDateChanged = (endTime) => {
    this.setState(
      {
        endTime,
      },
      this.validateTimeWindow
    );
  };

  openStartPicker = (event) => {
    this.startPicker.open(event);
  };

  openEndPicker = (event) => {
    this.endPicker.open(event);
  };

  onStartInputChange = (event) => {
    const updatedDateTime = parse(
      event.target.value,
      this.timeConfig.format,
      new Date()
    );
    if (isDateTimeValid(updatedDateTime)) {
      this.startDateChanged(updatedDateTime);
    } else {
      this.showMinTimeError();
      this.updateParentValue(appConstants.INVALID_TIME_RANGE);
    }
  };

  onEndInputChange = (event) => {
    const updatedDateTime = parse(
      event.target.value,
      this.timeConfig.format,
      new Date()
    );
    if (isDateTimeValid(updatedDateTime)) {
      this.endDateChanged(updatedDateTime);
    } else {
      this.showMinTimeError();
      this.updateParentValue(appConstants.INVALID_TIME_RANGE);
    }
  };

  validateTimeWindow = () => {
    let timeRange = "";
    if (!this.state.startTime && !this.state.endTime) {
      timeRange = null;
      this.showMinTimeError()
    } else if (
      isDateTimeValid(this.state.startTime) &&
      isDateTimeValid(this.state.endTime)
    ) {
      if (this.state.startTime > this.state.endTime) {
        timeRange = appConstants.INVALID_TIME_RANGE;
        this.showMinTimeError(
          `End Time should be greater than  ${format(
            this.state.startTime,
            this.timeConfig.format
          )}`
        );
      } else {
        timeRange = `${format(
          this.state.startTime,
          this.timeConfig.format
        )} - ${format(this.state.endTime, this.timeConfig.format)}`;
        this.showMinTimeError();
      }
    } else {
      timeRange = appConstants.INVALID_TIME_RANGE;
    }
    // Update the parent value
    this.updateParentValue(timeRange);
  };

  showMinTimeError = (errorMessage) => {
    if (!this.minTimeErrorElement) {
      this.minTimeErrorElement = document.createElement("p");
      this.minTimeErrorElement.className = `MuiFormHelperText-root MuiFormHelperText-error ${this.props.classes.errorMessage}`;
      if (
        this.endTimeInputRef &&
        this.endTimeInputRef.current &&
        this.endTimeInputRef.current.parentElement &&
        this.endTimeInputRef.current.parentElement.parentElement
      ) {
        this.endTimeInputRef.current.parentElement.parentElement.appendChild(
          this.minTimeErrorElement
        );
      }
    }
    if (errorMessage) {
      this.minTimeErrorElement.classList.add(
        this.props.classes.errorMessageVisible
      );
      this.minTimeErrorElement.textContent = errorMessage;
    } else {
      this.minTimeErrorElement.classList.remove(
        this.props.classes.errorMessageVisible
      );
    }
  };

  updateParentValue = (value) => {
    this.props.onChange?.(value);
  };

  getContainerClass = () => {
    const { classes, componentSpecificProps = {} } = this.props;
    const { isInputBorderRequired = true, roundInputBorder } = componentSpecificProps;
    if (roundInputBorder) {
      return classes.roundBorderContainer;
    }
    return isInputBorderRequired ? classes.editorContainer : classes.closedDayEditorContainer;
  };

  render() {
    const { classes, node } = this.props;
    const {
      isClosedDay = "",
      startLabel = null,
      endLabel = null,
      autoFocus = true,
      containerLabel = "",
    } = this.props?.componentSpecificProps ?? {};
    const containerClass = this.getContainerClass();
    return (
      <div
        className={containerClass}
        style={{ minHeight: node?.rowHeight }}
      >
        {containerLabel && <div className={classes.containerLabelClass}>{containerLabel}</div>}
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <div
            className={`theme-${this.props.theme.themeName} ${classes.container}`}
          >
            <InlineTimePicker
              keyboard
              clearable
              disableOpenOnEnter
              label={startLabel ?? "Start Time"}
              format={this.timeConfig.format}
              placeholder={this.timeConfig.placeholder}
              ampm={false}
              value={this.state.startTime}
              onChange={this.startDateChanged}
              onClear={this.startDateChanged}
              mask={this.timeConfig.inputMask}
              ref={(element) => (this.startPicker = element)}
              PopoverProps={{
                disablePortal: true,
              }}
              InputLabelProps={this.props.InputLabelProps ?? {
                classes: {
                  root: classes.labelProps,
                  shrink: classes.labelShrink,
                },
              }}
              InputProps={{
                autoFocus: autoFocus,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    classes={{ root: classes.inputAdornment }}
                  >
                    {
                      <Clear
                        classes={{ root: classes.iconColor }}
                        onClick={() => this.startDateChanged(null)}
                      />
                    }
                    {
                      <Time
                        classes={{ root: classes.iconColor }}
                        onClick={this.openStartPicker}
                      />
                    }
                  </InputAdornment>
                ),
                classes: {
                  root: classes.inputRoot,
                  input: isClosedDay
                    ? classes.closedDayInputStyle
                    : classes.inputStyle,
                  focused: classes.inputFocus,
                },
              }}
              classes={{
                  popoverPaper: classes.paperStyle,
              }}
              onInputChange={this.onStartInputChange}
              invalidDateMessage={`Invalid Time`}
            />
            <InlineTimePicker
              keyboard
              clearable
              disableOpenOnEnter
              label={endLabel ?? "End Time"}
              format={this.timeConfig.format}
              placeholder={this.timeConfig.placeholder}
              ampm={false}
              value={this.state.endTime}
              onChange={this.endDateChanged}
              onClear={this.endDateChanged}
              mask={this.timeConfig.inputMask}
              ref={(element) => (this.endPicker = element)}
              PopoverProps={{
                disablePortal: true,
              }}
              InputLabelProps={this.props.InputLabelProps ??{
                classes: {
                  root: classes.labelProps,
                  shrink: classes.labelShrink,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    classes={{ root: classes.inputAdornment }}
                  >
                    {
                      <Clear
                        classes={{ root: classes.iconColor }}
                        onClick={() => this.endDateChanged(null)}
                      />
                    }
                    {
                      <Time
                        classes={{ root: classes.iconColor }}
                        onClick={this.openEndPicker}
                      />
                    }
                  </InputAdornment>
                ),
                classes: {
                  root: classes.inputRoot,
                  input: isClosedDay
                    ? classes.closedDayInputStyle
                    : classes.inputStyle,
                  focused: classes.inputFocus,
                },
                inputRef: this.endTimeInputRef,
              }}
              classes={{
                  popoverPaper: classes.paperStyle
              }}
              onInputChange={this.onEndInputChange}
              invalidDateMessage={`Invalid Time`}
            />
          </div>
        </MuiPickersUtilsProvider>
      </div>
    );
  }
}

export default compose(withTheme(styles), withStyles(styles))(TimeRange);
